import { useCallback } from 'react';
import { push } from 'redux-first-router';
import { useTracking } from 'react-tracking';
import { useQueryClient } from '@tanstack/react-query';

import { useMount } from '@simplywallst/ui-core';

import { getPath } from '@/router/utils';

import {
  ROUTE_PORTFOLIO_DEFAULT,
  ROUTE_PORTFOLIO_NEW,
} from '@/constants/routes';
import { sentryCaptureMessage } from '@/components/Sentry';

import { PORTFOLIOS_QUERY_KEY } from '@/hooks/portfolio/usePortfolio';
import { AGGREGATE_PORTFOLIO_QUERY_KEY } from '@/hooks/portfolio/useAggregatePortfolio';
import { useReduxStateForPortfolioLinkingSuccess } from './hooks/useReduxStateForPortfolioLinkingSuccess';

import { SharesightLinkingSuccess } from './SharesightLinkingSuccess';
import { PlaidPortfolioLinkingSuccess } from '../../components/LinkingProviders/Plaid/PlaidPortfolioLinkingSuccess';

function goTo(type: string) {
  push(getPath({ type, payload: {} }));
}

const isValidProvider = (provider: string | undefined) => {
  return provider === 'sharesight' || provider === 'plaid';
};

export const PortfolioLinkingSuccess = () => {
  const { provider } = useReduxStateForPortfolioLinkingSuccess();
  const queryClient = useQueryClient();
  const { trackEvent } = useTracking({
    type: 'track',
    subject: 'page',
  });

  useMount(() => {
    if (!isValidProvider(provider)) {
      sentryCaptureMessage({
        team: 'wombats',
        message: 'Provider is not valid in PortfolioLinkingSuccess page url',
        extra: { provider },
      });
      goTo(ROUTE_PORTFOLIO_NEW);
    }
  });

  useMount(() => {
    trackEvent({
      action: 'landed',
      provider,
    });
  });

  const onSuccess = useCallback(() => {
    queryClient.invalidateQueries({
      queryKey: [PORTFOLIOS_QUERY_KEY],
    });
    queryClient.invalidateQueries({
      queryKey: [AGGREGATE_PORTFOLIO_QUERY_KEY],
    });
    goTo(ROUTE_PORTFOLIO_DEFAULT);
  }, [queryClient]);

  const onError = useCallback(() => {
    sentryCaptureMessage({
      team: 'wombats',
      message: 'Token exchange failed in PortfolioLinkingSuccess page',
      extra: { provider },
    });
    goTo(ROUTE_PORTFOLIO_NEW);
  }, [provider]);

  if (provider === 'sharesight')
    return <SharesightLinkingSuccess onSuccess={onSuccess} onError={onError} />;
  if (provider === 'plaid')
    return (
      <PlaidPortfolioLinkingSuccess onSuccess={onSuccess} onError={onError} />
    );

  return null;
};
